import { useId, type ReactNode } from 'react';
import { cn } from '@immo/ui-kit';
import { FLEET_METRICS_TIME_ZONE } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { clampPercent } from './health-figures';

export type ChartPoint = { readonly at: Date; readonly value: number | null };

type Props = {
  readonly title: string;
  readonly points: ReadonlyArray<ChartPoint>;
  readonly from: Date;
  readonly to: Date;
  readonly bucketMs: number;
  readonly yMax: number;
  readonly threshold?: { readonly value: number };
  readonly aside?: ReactNode;
  readonly formatValue: (value: number) => string;
};

const WIDTH = 1000;
const HEIGHT = 100;
const HOUR_MS = 3_600_000;
const X_LABELS = 4;
const HOUR_LABEL_MAX_SPAN_MS = 25 * HOUR_MS;

const HOUR_FORMAT = new Intl.DateTimeFormat('de-DE', {
  timeZone: FLEET_METRICS_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const DAY_FORMAT = new Intl.DateTimeFormat('de-DE', {
  timeZone: FLEET_METRICS_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
});

type Located = { readonly x: number; readonly y: number; readonly value: number };

/** A line chart on a fixed 0…yMax scale with the figure's limit dashed across it (IMM-1450). */
export function HealthLineChart({ title, points, from, to, bucketMs, yMax, threshold, aside, formatValue }: Props) {
  const t = useTranslations('pages.admin.fleet.health');
  const titleId = useId();
  const span = to.getTime() - from.getTime();

  const located: Located[] = [];

  for (const point of points) {
    if (point.value === null) continue;

    located.push({
      x: clampPercent(((point.at.getTime() - from.getTime()) / span) * 100),
      y: 100 - clampPercent((point.value / yMax) * 100),
      value: point.value,
    });
  }

  const segments = segmentsOf(points, from, span, yMax, bucketMs);
  const thresholdY = threshold === undefined ? null : 100 - clampPercent((threshold.value / yMax) * 100);
  const latest = located.at(-1);

  return (
    <div role="img" aria-labelledby={titleId} className="flex min-w-0 flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <span id={titleId} className="text-base-sm font-medium text-text-primary">
          {title}
        </span>
        {aside}
      </div>
      <div className="flex gap-2">
        <div className="relative h-24 w-12 shrink-0 font-mono text-xs text-text-tertiary" aria-hidden>
          <GutterLabel top={100} label={formatValue(0)} />
          <GutterLabel top={50} label={formatValue(yMax / 2)} />
          {threshold === undefined || thresholdY === null ? (
            <GutterLabel top={0} label={formatValue(yMax)} />
          ) : (
            <GutterLabel top={thresholdY} label={formatValue(threshold.value)} warning />
          )}
        </div>
        <div className="relative h-24 min-w-0 flex-1 border-b border-l border-border">
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            preserveAspectRatio="none"
            aria-hidden
            className="absolute inset-0 h-full w-full"
          >
            {located.length > 0 &&
              segments.map((segment) => (
                <g key={segment.key}>
                  <path d={segment.area} className="fill-info-50" data-segment="area" />
                  <path
                    d={segment.line}
                    className="fill-none stroke-info-500"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                    data-segment="line"
                  />
                </g>
              ))}
            {thresholdY !== null && (
              <line
                x1={0}
                x2={WIDTH}
                y1={thresholdY}
                y2={thresholdY}
                className="stroke-warning-500"
                strokeWidth={1}
                strokeDasharray="6 4"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          {located.length === 0 ? (
            <span className="absolute inset-0 flex items-center justify-center text-base-sm text-text-placeholder">
              {t('no-data')}
            </span>
          ) : (
            located.map((spot) => {
              const mark =
                threshold !== undefined && spot.value >= threshold.value
                  ? 'warning'
                  : spot === latest
                    ? 'latest'
                    : null;

              return mark === null ? null : (
                <span
                  key={`${spot.x}-${spot.y}`}
                  data-mark={mark}
                  className={cn(
                    'absolute h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full',
                    mark === 'warning' ? 'bg-warning-500' : 'bg-info-500',
                  )}
                  style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
                />
              );
            })
          )}
        </div>
      </div>
      {located.length > 0 && (
        <div className="relative ml-14 h-4 font-mono text-xs text-text-tertiary" aria-hidden>
          {axisLabels(from, to).map((label) => (
            <span
              key={label.time.getTime()}
              className="absolute -translate-x-1/2 whitespace-nowrap"
              style={{ left: `${((label.time.getTime() - from.getTime()) / span) * 100}%` }}
            >
              {label.text}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function GutterLabel({ top, label, warning = false }: { top: number; label: string; warning?: boolean }) {
  return (
    <span
      className={cn('absolute right-0 -translate-y-1/2 whitespace-nowrap', warning && 'text-warning-700')}
      style={{ top: `${top}%` }}
    >
      {label}
    </span>
  );
}

/** Hour labels up to 25 h (a one-day range across a DST change), otherwise day labels with a repeated day dropped. */
function axisLabels(from: Date, to: Date): Array<{ time: Date; text: string }> {
  const byHour = to.getTime() - from.getTime() <= HOUR_LABEL_MAX_SPAN_MS;
  const format = byHour ? HOUR_FORMAT : DAY_FORMAT;
  const labels = axisTimes(from, to).map((time) => ({ time, text: format.format(time) }));

  return byHour ? labels : labels.filter((label, index) => label.text !== labels[index - 1]?.text);
}

/** The first whole hour at or after `from`, then every quarter of the range. */
function axisTimes(from: Date, to: Date): Date[] {
  const first = Math.ceil(from.getTime() / HOUR_MS) * HOUR_MS;
  const step = (to.getTime() - from.getTime()) / X_LABELS;

  return Array.from({ length: X_LABELS }, (_, index) => new Date(first + index * step)).filter((time) => time < to);
}

/** Runs of non-null points no more than two buckets apart; a null or a wider gap breaks the line and its area. */
function segmentsOf(
  points: ReadonlyArray<ChartPoint>,
  from: Date,
  span: number,
  yMax: number,
  bucketMs: number,
): Array<{ key: number; line: string; area: string }> {
  const runs: Array<Array<{ at: number; x: number; y: number }>> = [];
  let previousAt: number | null = null;

  for (const point of points) {
    if (point.value === null) {
      previousAt = null;
      continue;
    }

    const at = point.at.getTime();
    const spot = {
      at,
      x: clampPercent(((at - from.getTime()) / span) * 100) * (WIDTH / 100),
      y: HEIGHT - clampPercent((point.value / yMax) * 100),
    };

    if (previousAt === null || at - previousAt > 2 * bucketMs) runs.push([spot]);
    else runs[runs.length - 1]?.push(spot);

    previousAt = at;
  }

  return runs
    .filter((run) => run.length > 1)
    .map((run) => {
      const line = run.map((spot, index) => `${index === 0 ? 'M' : 'L'}${spot.x} ${spot.y}`).join(' ');
      const first = run[0]!;
      const last = run[run.length - 1]!;

      return { key: first.at, line, area: `${line} L${last.x} ${HEIGHT} L${first.x} ${HEIGHT} Z` };
    });
}
